import React from 'react';

import { Typography } from '@mui/material';
import moment from 'moment';

import DateCell from 'components/FCTable/components/CoreCells/DateCell';
import MediaCell from 'components/FCTable/components/CoreCells/MediaCell';
import TextCell from 'components/FCTable/components/CoreCells/TextCell';
import FormCell from 'components/FCTable/components/ModuleCells/FormCell';
import Member from 'components/Member';
import DetailCellSkeleton from 'components/SkeletonScreen/components/DetailCellSkeleton';
import MemberCellSkeleton from 'components/SkeletonScreen/components/MemberCellSkeleton';
import TextCellSkeleton from 'components/SkeletonScreen/components/TextCellSkeleton';
import UserPendingTile from 'components/Tile/Status/UserMemberTile';
import UserTile from 'components/Tile/Status/UserTile';
import FormTemplate from 'containers/SuperAdmin/FormsTemplateLibrary/components/FormTemplate';
import globalMessages from 'translations/messages/global-messages';
import { GED, LOCATION_TYPES } from 'utils/constants';
import { getAnonymizedEmail } from 'utils/email';
import { objectByString } from 'utils/mappers';

import MemberCellWithUserId from './MemberCellWithUserId';
import PlanFolderCell from './PlanFolderCell';
import PlanItemCell from './PlanItemCell';
import SwitchCell from './SwitchCell';

const parseData = rowData => {
  const stringRowData = JSON.stringify(rowData, null, 2);
  return JSON.parse(stringRowData);
};

/**
 * Should be used when member is already existing in a list and
 * should not self-load itself
 * @see /app/containers/OneProjectPage/MembersPage/index.js
 */
// Black bold member with mail in first column
export const renderLoadedMemberCell = params => {
  const { rowData, columnData, isRestrictedInformation, currentUserId } = params;
  const parsedRowData = parseData(rowData);
  const { loading } = columnData.loading || {};

  if (isRestrictedInformation) {
    if (parsedRowData?.email != null && parsedRowData?.user_id !== currentUserId) {
      parsedRowData.email = getAnonymizedEmail(parsedRowData.email);
    }
  }

  if (loading) {
    return <MemberCellSkeleton />;
  }

  if (!Object.keys(parsedRowData).length) {
    return null;
  }

  return (
    <Member member={parsedRowData} isFirstColumnInTable>
      <UserPendingTile showTooltip user={parsedRowData} />
      {parsedRowData.user_id === currentUserId && <UserTile showTooltip user={parsedRowData} />}
    </Member>
  );
};

export const renderMemberCellWithUserId = params => {
  const { dataKey, rowData } = params;
  const parsedRowData = parseData(rowData);
  const { loading } = params?.columnData?.loading || {};
  const keyValue = objectByString(parsedRowData, dataKey);
  return <MemberCellWithUserId userId={keyValue} loading={loading} />;
};

export const renderSourceCell = params => {
  const { dataKey, rowData, className, mixedLabel, intl } = params;
  const parsedRowData = parseData(rowData);
  const { loading } = params?.columnData || {};

  let keyValue =
    parsedRowData.type === LOCATION_TYPES.folder ? null : objectByString(parsedRowData, dataKey);

  if (keyValue?.split(';').length > 2) {
    keyValue = mixedLabel;
  }
  const sanitizedKeyValue = keyValue?.endsWith(';') ? keyValue.slice(0, -1) : keyValue;
  const keyValueIntl =
    sanitizedKeyValue === GED ? intl.formatMessage(globalMessages.ged) : sanitizedKeyValue;
  return (
    <TextCell
      loading={loading}
      mainText={keyValueIntl}
      mainTextVariant="subtitleLight"
      className={className}
    />
  );
};

export const renderDateCell = params => {
  const { cellData, imperative = false, showIcon, columnData } = params;
  const daysLeft = moment(cellData)
    .startOf('day')
    .diff(moment().endOf('day'), 'days');

  return (
    <DateCell
      date={cellData}
      loading={columnData?.loading}
      hasIcon={showIcon}
      isUrgent={imperative && daysLeft < 0}
      mainTextVariant="subtitleLight"
    />
  );
};

export const renderTextCell = params => {
  const { dataKey, rowData, description } = params;
  const parsedRowData = parseData(rowData);
  const { loading } = params?.columnData?.loading || {};
  const keyValue = objectByString(parsedRowData, dataKey);

  return <TextCell loading={loading} mainText={keyValue} description={description} />;
};

export const renderMediaCell = params => {
  const { cellData = {}, columnData } = params;

  const { loading } = columnData.loading || {};
  const stringRowData = JSON.stringify(cellData, null, 2);
  const parsedRowData = JSON.parse(stringRowData);

  return <MediaCell media_resource={parsedRowData} loading={loading} />;
};

export const renderSwitchCell = params => {
  const { rowData, cellData, onValueChange, disabled, columnData } = params;
  const parsedRowData = parseData(rowData);
  const { loading } = columnData.loading || {};

  if (loading) {
    return <DetailCellSkeleton />;
  }

  return (
    <SwitchCell
      value={cellData || false}
      onValueChange={(_, value) => onValueChange(value, parsedRowData)}
      loading={loading}
      disabled={disabled}
    />
  );
};

export const renderPlanFolder = params => {
  const { rowData, columnData } = params;

  const stringRowData = JSON.stringify(rowData, null, 2);
  const parsedRowData = JSON.parse(stringRowData);

  const { loading } = columnData.loading || {};

  if (loading) {
    return <TextCellSkeleton />;
  }

  if (!Object.keys(parsedRowData).length) {
    return null;
  }

  return <PlanFolderCell item={parsedRowData} />;
};

export const renderPlanItem = params => {
  const { rowData, columnData } = params;

  const stringRowData = JSON.stringify(rowData, null, 2);
  const parsedRowData = JSON.parse(stringRowData);

  const { loading } = columnData.loading || {};

  if (loading) {
    return <TextCellSkeleton />;
  }

  if (!Object.keys(parsedRowData).length) {
    return null;
  }

  return (
    <PlanItemCell
      type={parsedRowData.type || parsedRowData?.plan_type?.toLowerCase()}
      name={parsedRowData.name}
      plan={parsedRowData}
      listsRequestOption={columnData}
    />
  );
};

export const renderForm = param => {
  const { members, rowData, columnData } = param;
  const stringRowData = JSON.stringify(rowData, null, 2);
  const parsedRowData = JSON.parse(stringRowData);
  const { loading } = columnData.loading || {};

  if (loading) {
    return <DetailCellSkeleton />;
  }
  const memberFound = members.find(member => member.user_id === parsedRowData.assignee_id);

  if (!Object.keys(parsedRowData).length) {
    return null;
  }
  return <FormCell form={parsedRowData} user={memberFound} />;
};

export const renderFormTemplate = params => {
  const { rowData, columnData } = params;
  const stringRowData = JSON.stringify(rowData, null, 2);
  const parsedRowData = JSON.parse(stringRowData);
  const { loading } = columnData.loading || {};

  if (loading) {
    return <DetailCellSkeleton />;
  }

  if (!Object.keys(parsedRowData).length) {
    return null;
  }

  return (
    <FormTemplate
      name={parsedRowData.form_template_name}
      createdAt={parsedRowData.created_at}
      state={parsedRowData.state}
    />
  );
};

export const renderLanguage = params => {
  const { rowData, columnData } = params;
  const stringRowData = JSON.stringify(rowData, null, 2);
  const parsedRowData = JSON.parse(stringRowData);
  const { loading } = columnData.loading || {};

  if (loading) {
    return <TextCellSkeleton />;
  }

  if (!Object.keys(parsedRowData).length) {
    return null;
  }
  return (
    <Typography variant="subtitleLight">
      {(parsedRowData.form_languages || []).map(item => item.language).join(', ')}
    </Typography>
  );
};
